import { createStore } from 'vuex';
import createPersistedState from 'vuex-persistedstate';
import SecureLS from 'secure-ls';
import AuthApi from '@/api/AuthApi';
import SettingsApi from '@/api/SettingsApi';

/**
 * Session store for the admin SPA.
 * Persisted under localStorage key `webwalker` via secure-ls (AES).
 * If `VUE_APP_LS_ENCRYPTION_SECRET` is empty (as in committed .env.production),
 * secure-ls uses its library default — treat as obfuscation, not strong encryption.
 * Decrypt failure clears the blob so admins can re-login.
 */
const ls = new SecureLS({
  encodingType: 'aes',
  isCompression: false,
  encryptionSecret: process.env.VUE_APP_LS_ENCRYPTION_SECRET,
});

const store = createStore({
  plugins: [
    createPersistedState({
      key: 'webwalker',
      storage: {
        getItem: (key) => {
          try {
            return ls.get(key);
          } catch (err) {
            ls.clear();
            return ls.get(key);
          }
        },
        setItem: (key, value) => ls.set(key, value),
        removeItem: (key) => ls.remove(key),
      },
    }),
  ],
  state: {
    token: false, // Core API Bearer token (truthy string when logged in)
    admin: false, // Current admin profile from GET /admin
    isDarkMode: false,
    reverseMessageOrder: false, // Conversation message order preference
    isMaintenance: false, // Set on 503 from /admin; triggers reload
    errors: [], // Transient operator-facing error stack
  },
  getters: {
    /** Authenticated = truthy token only (no expiry check client-side). */
    authenticated(state) {
      return !!state.token;
    },
  },
  mutations: {
    setToken(state, token) {
      state.token = token;
    },
    setAdmin(state, admin) {
      state.admin = admin;
    },
    setDarkMode(state, isDarkMode) {
      state.isDarkMode = isDarkMode;
    },
    setReverseMessageOrder(state, reverseMessageOrder) {
      state.reverseMessageOrder = reverseMessageOrder;
    },
    setMaintenance(state, isMaintenance) {
      state.isMaintenance = isMaintenance;
    },
    pushErrors(state, errors) {
      state.errors.push(errors);
      if (
        state.errors.length > 3 ||
        (typeof window !== 'undefined' &&
          window.innerWidth < 960 &&
          state.errors.length > 1)
      ) {
        state.errors.shift();
      }
    },
    clearErrors(state) {
      state.errors = [];
    },
  },
  actions: {
    /**
     * Refresh current admin from Core.
     * - Success: commit admin (+ dark_mode from settings when present).
     * - 503: flip isMaintenance and reload once.
     * - Other failures: clear all localStorage (forces re-login on next load).
     */
    loadAdmin({ commit, state }) {
      return AuthApi.auth()
        .then((response) => {
          commit('setAdmin', response.data.data);

          if (response.data.data.settings) {
            commit('setDarkMode', response.data.data.settings.dark_mode);
          }
        })
        .catch((error) => {
          if (error.response && error.response.status == 503) {
            if (!state.isMaintenance) {
              commit('setMaintenance', true);
              window.location.reload();
            }
          } else {
            localStorage.clear();
          }
        });
    },
    switchDarkMode({ commit }, isDarkMode) {
      SettingsApi.update({
        settings: { dark_mode: isDarkMode },
      });
      commit('setDarkMode', isDarkMode);
    },
    switchReverseMessageOrder({ commit }, reverseMessageOrder) {
      SettingsApi.update({
        settings: { reverse_message_order: reverseMessageOrder },
      });
      commit('setReverseMessageOrder', reverseMessageOrder);
    },
    pushErrors({ commit }, errors) {
      commit('pushErrors', errors);
    },
    clearErrors({ commit, state }) {
      if (state.errors.length) {
        commit('clearErrors');
      }
    },
  },
});

export default store;
